import { useEffect, useRef, useState } from "react";

import { api, type Anomalie, type Classe } from "./api";

export default function ImportQcm({ surImport }: { surImport: () => void }) {
  const [classes, setClasses] = useState<Classe[]>([]);
  const [classeId, setClasseId] = useState<number | null>(null);
  const [titre, setTitre] = useState("");
  const [date, setDate] = useState("");
  const [versions, setVersions] = useState(4);
  const [anomalies, setAnomalies] = useState<Anomalie[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [encours, setEncours] = useState(false);
  const fichier = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.listerClasses().then((liste) => {
      setClasses(liste);
      if (liste.length > 0) setClasseId(liste[0].id);
    });
  }, []);

  const importer = async (choisi: File) => {
    if (classeId === null) return;
    setEncours(true);
    setErreur(null);
    setAnomalies(null);
    try {
      const bilan = await api.importerQcm(classeId, choisi, titre, date, versions);
      setAnomalies(bilan.anomalies);
      surImport();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEncours(false);
    }
  };

  if (classes.length === 0) {
    return (
      <p className="aide">
        Créez d'abord une classe dans l'onglet <strong>Classes</strong> : un QCM
        s'y rattache.
      </p>
    );
  }

  return (
    <div className="import-ds">
      <h3>Importer un fichier de questions</h3>
      <div className="actions">
        <select
          value={classeId ?? ""}
          onChange={(e) => setClasseId(Number(e.target.value))}
        >
          {classes.map((classe) => (
            <option key={classe.id} value={classe.id}>
              {classe.code} — {classe.niveau_libelle}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          placeholder="Titre (facultatif)"
        />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          title="Date de passation (facultative)"
        />
        <label className="versions" title="Nombre de sujets différents à composer">
          <select
            value={versions}
            onChange={(e) => setVersions(Number(e.target.value))}
          >
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n} version{n > 1 ? "s" : ""}
              </option>
            ))}
          </select>
        </label>
        <input
          ref={fichier}
          type="file"
          accept=".typ,text/plain"
          hidden
          onChange={(e) => {
            const choisi = e.target.files?.[0];
            if (choisi) importer(choisi);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => fichier.current?.click()}
          disabled={encours}
        >
          {encours ? "Lecture en cours…" : "Choisir un fichier .typ"}
        </button>
      </div>

      {erreur && <p className="erreur">{erreur}</p>}

      {anomalies !== null && anomalies.length === 0 && (
        <p className="message">Questions versées en banque, QCM créé.</p>
      )}
      {anomalies?.map((anomalie, rang) => (
        <p
          key={rang}
          className={anomalie.gravite === "erreur" ? "erreur" : "message"}
        >
          {anomalie.message}
        </p>
      ))}
    </div>
  );
}
